import { isFulfilled } from "@reduxjs/toolkit";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { NOTIFICATION_TIME_OUT } from "../../../../constants/app";
import useFileNameLength from "../../../../hooks/useFileNameLength";
import { DOCUMENT_CONFIGS } from "../../../../models/superadmin";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { CandidateStorage } from "../../../../services/CandidateStorage";
import { useAppDispatch } from "../../../../store";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../../../store/application/slice";
import { declineMakeARequestThunk, profileEditRequestStatusThunk } from "../../../../store/superadmin/thunks";
import { ICandidateDetails, TProfileRequestResponse } from "../../../../store/superadmin/types";
import { downloadFile, getFormatedDate } from "../../../../utils/dataParser";
import { getDateLabel, getMaxDateByDocumentType, getMinDateByDocumentType } from "../../../../utils/documentHelper";
import { formatFileName } from "../../../../utils/stringUtils";
import CustomButton from "../../../UI/Buttons/CustomButton";
import CircleTick from "../../../UI/Icons/CircleTick";
import Download from "../../../UI/Icons/Download";
import InactiveOutline from "../../../UI/Icons/InactiveOutline";
import RightArrow from "../../../UI/Icons/RightArrow";
import CustomDateInput from "../../../UI/Inputs/DateInput";

interface ProfileEditRequestProps {
  requestList: TProfileRequestResponse[];
  candidateId?: number; // Optional prop to pass candidate ID if needed
  reviewCandidateData: ICandidateDetails | null;
  setShowModal: (showModal: boolean) => void;
  getAllCandidates?: () => void;
}

const ProfileEditRequest: React.FC<ProfileEditRequestProps> = ({
  requestList,
  candidateId,
  reviewCandidateData,
  setShowModal,
  getAllCandidates
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { maxLength, preserveEndChars } = useFileNameLength();
  const [visibleRequests, setVisibleRequests] = useState(requestList);
  const [selectedDates, setSelectedDates] = useState<Record<string, Date | null>>({});

  /**
   *  Hanlde the decline the candidate make a request from the candidate  my profile page
   */
  const handleDeclineRequest = async (
    request_id: string,
    request_from: string
  ) => {
    const activateResponse = await dispatch(
      declineMakeARequestThunk({ request_id, request_from })
    );
    if (isFulfilled(activateResponse)) {
      // Remove the declined request from the visible list when declined
      setVisibleRequests((prev) =>
        prev.filter((request) => request.request_id !== request_id)
      );
      toast.success("Request declined successfully.");
    }
  };

  useEffect(() => {
    CandidateStorage.save(candidateId ?? 0); // Save candidate ID to session storage
  }, [candidateId]);

  const handleApproveRequest = (requestId: string, requestFrom: string) => {
    CandidateStorage.save(candidateId ?? 0); // Save candidate ID to session storage
    localStorage.setItem("request_id", requestId);
    localStorage.setItem("request_from", requestFrom);

    navigate(AUTH_ROUTE.ADMIN_CANDIDATE_PROFILE);
  };

  const handleApproveKeyDcoumentRequest = async(requestId: string, requestFrom: string) => {
    dispatch(showTransparentPreloaderAction())
      const response = await dispatch(profileEditRequestStatusThunk({request_id: requestId, request_from: requestFrom}));
      if(isFulfilled(response)){
        setShowModal?.(false)
        dispatch(showNotificationAction("Approved"))
        setTimeout(() => {
          getAllCandidates?.()
        }, NOTIFICATION_TIME_OUT);
      }
    dispatch(hideTransparentPreloaderAction())
  }

  return (
    <>
      {visibleRequests.length > 0
        ? visibleRequests.map((requestData, index) => {
          const documentTypeValue = requestData?.document_type;
            const documentDate = requestData?.document_date;
            // const documentDate = "2025-10-29"; // Temporary hardcoded date for testing
            const docTypeName = DOCUMENT_CONFIGS.find((docList) => docList.type === documentTypeValue);
            const documentType = docTypeName
              ? docTypeName.title
              : typeof documentTypeValue === "string"
                ? documentTypeValue.replace(/_/g, " ")
                : "";
                
            return (
              <div key={"requestData" + requestData?.request_id}>
                <div className="bg-teal-pale border border-teal-dark radius-8px p-20px d-flex flex-column gap-10">
                  <div className="fs-13 text-teal-dark">
                    {getFormatedDate(requestData?.attachments?.[0]?.created_at)}
                  </div>
                  {documentTypeValue === null ||
                  documentTypeValue === undefined ? (
                    <div className="fs-16 fw-700 text-teal-dark">
                      Employee has submitted the below request to edit their
                      profile:
                    </div>
                  ) : null}
                  {documentTypeValue !== null &&
                  documentTypeValue !== undefined ? (
                    <div className="fs-16">
                      <span className="fw-700 text-teal-dark">
                        Employee has submitted the below document for:
                      </span>{" "}
                      <span className="text-teal-dark">{documentType}</span>
                    </div>
                  ) : null}
                  <div className="fs-16 text-black">
                    {requestData?.details ?? ""}
                  </div>
                  <div className="d-flex flex-column gap-5">
                    <div className="fs-13 text-grey-medium">
                      Employee's Uploaded Documents:
                    </div>
                    {requestData?.attachments?.length > 0 ? (
                      requestData?.attachments?.map((attachment, index) => {
                        return (
                          <div
                            className="d-flex currsor-pointer"
                            onClick={() => {
                              downloadFile(
                                attachment?.signed_url ?? "",
                                attachment?.file_name ?? ""
                              );
                            }}
                          >
                            <div
                              key={"attachment" + index}
                              className="bg-off-white radius-8px px-20px py-8px d-flex gap-10 align-items-center fs-16 fw-400"
                            >
                              <Download />
                              <span className="text-black lh-sm text-break">
                                {formatFileName(attachment?.file_name ?? "", maxLength, preserveEndChars)}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="fs-13 text-grey-medium">
                        No documents uploaded.
                      </div>
                    )}
                  </div>

                  {/**
                   * Show document issue date only if available
                   */}
                  {documentDate !== null && documentDate !== undefined && (
                    <div className="d-flex gap-10 align-items-center">
                      <div className="fs-13 fw-400 text-grey-dark">
                        {getDateLabel(documentTypeValue ?? "")}:
                      </div>
                      <div className="w-200px">
                        <DatePicker
                          selected={
                            selectedDates[requestData.request_id] ??
                            (documentDate ? new Date(documentDate) : null)
                          }
                          onChange={(date) => {
                            setSelectedDates((prev) => ({
                              ...prev,
                              [requestData.request_id]: date,
                            }));
                          }}
                          customInput={
                            <CustomDateInput
                              placeholder="Select a date..."
                              value={
                                selectedDates[requestData.request_id]
                                  ? selectedDates[requestData.request_id]?.toLocaleDateString(undefined, {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : ""
                              }
                            />
                          }
                          dateFormat="dd MMM yyyy"
                          minDate={getMinDateByDocumentType(documentTypeValue)}
                          maxDate={getMaxDateByDocumentType(documentTypeValue)}
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          disabled={true}
                        />
                      </div>
                    </div>
                  )}
                  <div
                    className={`d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-end ${
                      requestData?.status === "pending" ? "" : "d-none"
                    }`}
                  >
                    <CustomButton
                      variant={"primary"}
                      type="button"
                      className="btn-theme bg-off-white text-black"
                      onClick={() =>
                        handleDeclineRequest(
                          requestData?.request_id,
                          requestData?.request_from
                        )
                      }
                    >
                      <InactiveOutline />
                      Decline Request
                    </CustomButton>

                    {requestData?.request_from !== "key_documents" && (
                    <CustomButton
                      variant={"primary"}
                      type="button"
                      className="btn-theme bg-teal text-black"
                      onClick={() => {
                        handleApproveRequest(
                          requestData?.request_id,
                          requestData?.request_from
                        );
                      }}
                    >
                      Approve Request and Edit
                      <RightArrow color="black" />
                    </CustomButton>
                    )}

                    {requestData?.request_from === "key_documents" && (
                      <CustomButton
                        variant={"primary"}
                        type="button"
                        className="btn-theme bg-teal text-black"
                        onClick={() => {
                          handleApproveKeyDcoumentRequest(
                            requestData?.request_id,
                            requestData?.request_from
                          );
                        }}
                      >
                        <CircleTick fill="black" />
                        Approve Request
                      </CustomButton>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        : null}
    </>
  );
};

export default ProfileEditRequest;
